import type { ReactiveController, ReactiveControllerHost } from 'lit';
import { History } from '@o2d/history';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import Konva from 'konva';

import type { WhiteboardBus } from '../../core/bus/whiteboard-bus.js';
import type { UiAction } from '../../core/bus/ui-action.events.js';
import { objectId, type ObjectId } from '../../core/bus/branded-types.js';
import type { BoardState } from '../../core/state/board-state.js';
import { UpdatePropertyCommand } from '../../core/command/update-property.command.js';
import {
    computeStickyFontSize,
    stickyPaddingFor,
} from './sticky.renderer.js';
import { isStickyNote } from './sticky.types.js';

/**
 * Optional bridge into the host element for visual side-effects that the
 * controller alone cannot perform: hiding the underlying Konva text node
 * while editing and triggering a content-layer redraw. Tests may omit
 * these — the controller falls back to DOM-only behaviour.
 */
export interface StickyEditorBindings {
    konvaNodeFor(id: ObjectId): Konva.Node | undefined;
    redrawContent(): void;
}

/**
 * Lit ReactiveController that owns the sticky text-editor DOM surface (TR7).
 *
 * Subscribes to `bus.uiActions$` and translates `edit/enter-sticky`,
 * `edit/commit` and `edit/cancel` into open/close of a `<div contentEditable>`
 * overlay. Live-refits the font size on input, hides the underlying Konva
 * text while editing, and dispatches commit/cancel UiActions on blur,
 * Enter and Escape keys.
 *
 * Edit-FSM transitions are owned by `EditModeController` (TR15); this
 * controller is concerned only with the DOM surface and the resulting
 * `UpdatePropertyCommand` history push.
 *
 * FC10: enter for unknown / non-sticky ids is rejected silently (no throw).
 * FC11: bus subscription errors are swallowed with DEV warning.
 */
export class StickyEditorController implements ReactiveController {
    readonly #host: ReactiveControllerHost & HTMLElement;
    readonly #bus: WhiteboardBus;
    readonly #state: BoardState;
    readonly #history: History<BoardState>;
    readonly #bindings: StickyEditorBindings | null;
    readonly #disconnect$ = new Subject<void>();

    #editor: HTMLDivElement | null = null;
    #editingId: ObjectId | null = null;
    #onDocMouseDown: ((e: MouseEvent) => void) | null = null;
    #readonly = false;

    constructor(
        host: ReactiveControllerHost & HTMLElement,
        bus: WhiteboardBus,
        state: BoardState,
        history: History<BoardState>,
        bindings: StickyEditorBindings | null = null,
    ) {
        this.#host = host;
        this.#bus = bus;
        this.#state = state;
        this.#history = history;
        this.#bindings = bindings;
    }

    hostConnected(): void {
        this.#bus.uiActions$
            .pipe(takeUntil(this.#disconnect$), takeUntil(this.#bus.destroy$))
            .subscribe({
                next: (action) => this.#handle(action),
                error: () => {
                    if (import.meta.env?.DEV) {
                        console.warn('[StickyEditorController] bus error swallowed');
                    }
                },
            });

        // Capture-phase mousedown ensures the editor commits BEFORE Konva
        // processes the click — otherwise a click on empty canvas would
        // clear selection before the blur-commit fires.
        this.#onDocMouseDown = (e: MouseEvent): void => {
            if (!this.#editor || !this.#editingId) return;
            const target = e.target as Node | null;
            if (this.#editor.contains(target)) return;
            // Whitelist: clicks on the optionsbar must not commit.
            if (target instanceof Element && target.closest('o2d-svg-optionsbar')) return;
            this.#dispatchCommit();
        };
        document.addEventListener('mousedown', this.#onDocMouseDown, true);
    }

    hostDisconnected(): void {
        if (this.#onDocMouseDown) {
            document.removeEventListener('mousedown', this.#onDocMouseDown, true);
            this.#onDocMouseDown = null;
        }
        this.#disconnect$.next();
        this.#disconnect$.complete();
        this.#removeEditorDom();
        this.#editingId = null;
    }

    #handle(action: UiAction): void {
        switch (action.type) {
            case 'edit/enter-sticky':
                this.#openEditor(action.id);
                return;
            case 'edit/commit':
                this.#commit(action.id, action.text);
                return;
            case 'edit/cancel':
                this.#cancel(action.id);
                return;
            default:
                return;
        }
    }

    /**
     * TR7 defense-in-depth: when the host is in readonly mode, the editor
     * must never open. The primary guard lives in EditModeController, but a
     * direct UiAction emit could otherwise still trigger this surface.
     * If invoked while editing, the open editor is closed without committing.
     */
    setReadonly(value: boolean): void {
        this.#readonly = value;
        if (value && this.#editingId !== null) {
            this.#closeEditor();
        }
    }

    #openEditor(id: ObjectId): void {
        if (this.#readonly) return;
        const obj = this.#state.getObject(id);
        // FC10: silently reject unknown / non-sticky ids.
        if (!obj || !isStickyNote(obj)) return;

        this.#removeEditorDom();

        const editor = document.createElement('div');
        editor.className = 'sticky-editor';
        editor.contentEditable = 'true';
        editor.spellcheck = false;
        editor.textContent = obj.text;

        this.#positionEditor(editor, obj);

        const container = this.#host.querySelector<HTMLElement>('.board-root') ?? this.#host;
        container.appendChild(editor);

        this.#editor = editor;
        this.#editingId = id;

        // Hide the Konva text while editing so the user sees only the
        // contentEditable overlay (prevents double-text ghosting).
        const node = this.#bindings?.konvaNodeFor(id);
        if (node instanceof Konva.Group) {
            node.findOne<Konva.Text>('.sticky-text')?.visible(false);
            this.#bindings?.redrawContent();
        }

        editor.addEventListener('blur', () => this.#dispatchCommit());
        editor.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') { e.preventDefault(); this.#dispatchCancel(); return; }
            // Tab beendet die Bearbeitung (wie im Textfeld) — so bleibt Enter frei
            // fuer Zeilenumbrueche. Klick daneben (blur) committet ebenfalls.
            if (e.key === 'Tab') { e.preventDefault(); this.#dispatchCommit(); return; }
            // Enter (mit oder ohne Shift) macht einen Zeilenumbruch statt zu
            // committen (Anke, 2026-07-28). insertLineBreak fuegt zuverlaessig ein
            // <br> ein — konsistent ueber Browser und verlaesslich auch fuer
            // Leerzeilen (mehrfaches Enter).
            if (e.key === 'Enter') {
                e.preventDefault();
                // insertLineBreak fuegt zuverlaessig ein <br> ein (auch fuer
                // Leerzeilen) und setzt den Cursor korrekt. In Testumgebungen ohne
                // execCommand (happy-dom) ist es ein No-op — dort ist der reine
                // Browser-Pfad nicht relevant; der Umbruch wird per E2E geprueft.
                try {
                    document.execCommand('insertLineBreak');
                } catch {
                    // Umgebung ohne execCommand — im Browser nie der Fall.
                }
                this.#refit();
            }
        });
        editor.addEventListener('input', () => this.#refit());

        editor.focus();
        const range = document.createRange();
        range.selectNodeContents(editor);
        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);

        // Calibrate against the editor's real DOM metrics so existing text
        // doesn't scroll the editor internally on open.
        this.#refit();
    }

    #commit(id: ObjectId, text: string): void {
        const target = this.#state.getObject(id);
        if (!target || !isStickyNote(target)) {
            this.#closeEditor();
            return;
        }
        if (target.text !== text) {
            const cmd = new UpdatePropertyCommand(id, 'text', text);
            this.#history.push(cmd, this.#state);
        }
        this.#closeEditor();
    }

    #cancel(_id: ObjectId): void {
        this.#closeEditor();
    }

    #dispatchCommit(): void {
        const id = this.#editingId;
        const editor = this.#editor;
        if (!id || !editor) return;
        // Nur EINEN abschliessenden Umbruch entfernen (technischer Rest aus
        // contentEditable), damit bewusst gesetzte Leerzeilen erhalten bleiben.
        const text = editor.innerText.replace(/\n$/, '');
        this.#bus.uiActions$.next({ type: 'edit/commit', id: objectId(id), text });
    }

    #dispatchCancel(): void {
        const id = this.#editingId;
        if (!id) return;
        this.#bus.uiActions$.next({ type: 'edit/cancel', id: objectId(id) });
    }

    #closeEditor(): void {
        const id = this.#editingId;
        this.#removeEditorDom();
        this.#editingId = null;

        if (id == null) return;
        const node = this.#bindings?.konvaNodeFor(id);
        if (node instanceof Konva.Group) {
            node.findOne<Konva.Text>('.sticky-text')?.visible(true);
            this.#bindings?.redrawContent();
        }
    }

    #removeEditorDom(): void {
        const editor = this.#editor;
        if (!editor) return;
        this.#editor = null;
        // Guard against the editor having already been detached (e.g. by a
        // re-render of the host template, or by a synchronous blur handler
        // running after `.remove()` has begun). `Element.remove()` is a no-op
        // when the node has no parent, but defensive null-out first prevents
        // re-entrant blur → dispatchCommit paths from observing a stale ref.
        if (editor.parentNode) editor.remove();
    }

    #positionEditor(editor: HTMLDivElement, obj: { id: ObjectId; x: number; y: number; width: number; height: number }): void {
        const vp = this.#state.viewport;
        const padding = stickyPaddingFor(obj.width, obj.height);
        const innerPadding = padding * vp.scale;
        const stageX = obj.x * vp.scale + vp.x;
        const stageY = obj.y * vp.scale + vp.y;
        editor.style.left   = `${stageX + innerPadding}px`;
        editor.style.top    = `${stageY + innerPadding}px`;
        editor.style.width  = `${obj.width  * vp.scale - innerPadding * 2}px`;
        editor.style.height = `${obj.height * vp.scale - innerPadding * 2}px`;

        const node = this.#bindings?.konvaNodeFor(obj.id);
        if (node instanceof Konva.Group) {
            const konvaText = node.findOne<Konva.Text>('.sticky-text');
            if (konvaText) editor.style.fontSize = `${konvaText.fontSize() * vp.scale}px`;
        }
    }

    #refit(): void {
        const editor = this.#editor;
        const id = this.#editingId;
        if (!editor || !id) return;
        const obj = this.#state.getObject(id);
        if (!obj || !isStickyNote(obj)) return;

        // EXAKT dieselbe (Konva-basierte) Groessenberechnung wie die gerenderte
        // Notiz — so brechen Editor und Anzeige garantiert an denselben Stellen
        // um (Anke, 2026-07-28). Der Live-Text kommt aus dem Editor; nur ein
        // technischer Rest-Umbruch wird entfernt (bewusste Leerzeilen bleiben).
        // Die Weltgroesse wird auf Bildschirmpixel skaliert.
        const text = editor.innerText.replace(/\n$/, '');
        const worldFont = computeStickyFontSize(obj.width, obj.height, text);
        editor.style.fontSize = `${worldFont * this.#state.viewport.scale}px`;
    }
}
